import { type AgentName } from "@/lib/agentcore-client"
import { type HowToUseContent } from "./HowToUseDialog"

/**
 * Per-agent "How To Use" guide content.
 *
 * This is a partial registry: an agent shows a "How To Use" button in the chat
 * UI only when it has an entry here. To add a guide for another agent, add a new
 * key with its {@link HowToUseContent} — no other wiring is required.
 */
export const HOW_TO_USE_CONTENT: Partial<Record<AgentName, HowToUseContent>> = {
  "survey-variable-discovery": {
    title: "How To Use: Survey Variable Discovery Agent",
    intro:
      "This agent helps you find survey variables across MAP datasets and build a custom " +
      "extraction — a subset of variables pulled from the source data files for your analysis. " +
      "Follow these steps to go from a question to downloadable output files.",
    steps: [
      {
        title: "1. Narrow down to the surveys of interest",
        body:
          "Start by telling the agent which surveys you care about — for example a country, program, and year (\"Kenya DHS 2022\") or a broader set (\"all MICS surveys in West Africa\"). The agent can list the countries and surveys that have data available so you can focus your search on the right datasets before looking at variables.",
      },
      {
        title: "2. Ask the agent to find variables",
        body:
          "Within those surveys, describe the topic or indicator you're interested in (for example, \"bed net ownership\" or \"maternal education\"). The agent searches survey metadata across DHS, MICS, LSMS, and SPA and returns matching variables with their labels and value codes.",
      },
      {
        title: "3. Inspect the variable details",
        body:
          "Each result appears as an inline card (single variable) or a compact grid (multiple variables). Review the variable name, human-readable label, the survey and file it comes from, and its value mappings before deciding to include it.",
      },
      {
        title: "4. Add variables to your extraction",
        body:
          "Click \"Add To Extraction\" on any variable card or grid row. Added variables accumulate in the Extractions panel on the right, grouped by survey and then by source file.",
      },
      {
        title: "5. Review and customize in the Extractions panel",
        body:
          "In the right-side panel you can rename the output CSV for each file, rename a variable's output column (label rename), and remap value codes to friendlier descriptions (value recode). Remove any variable with its X button. There is one extraction per chat session.",
      },
      {
        title: "6. Create the extraction",
        body:
          "Click \"Create Extraction\" to run the extraction. When it finishes, a toast notification gives you an S3 Console link to the output files. The button then becomes \"Update Extraction\" — click it again any time to re-run with your current variable list.",
      },
    ],
    footnote:
      "Tip: there is only one extraction per chat session. Start a new conversation if you want " +
      "a fresh, separate extraction.",
  },
}

/**
 * Look up the "How To Use" guide content for an agent, if one is defined.
 *
 * @param agent - The agent to look up guide content for.
 * @returns The agent's {@link HowToUseContent}, or undefined when the agent has
 *   no guide and therefore should not show a "How To Use" button.
 */
export function getHowToUseContent(agent: AgentName): HowToUseContent | undefined {
  return HOW_TO_USE_CONTENT[agent]
}
